// I/O Supabase pour le calendrier (EPIC E PR1). RLS applique l'isolation.
import { supabase } from '../../lib/supabaseClient';
import { listAppointments } from '../dossiers/appointmentService';
import type { AppointmentSrc, DossierSrc, HearingSrc, TaskSrc } from './calendarAggregate';

export interface CalendarSources {
  dossiers: DossierSrc[];
  tasks: TaskSrc[];
  hearings: HearingSrc[];
  appointments: AppointmentSrc[];
}

/**
 * Fetch des sources calendrier pour le collaborateur courant. Dossiers actifs
 * (non archivés, non clos) ; tâches assignées via task_assignments. RLS applique
 * l'isolation par dossier — aucune policy nouvelle.
 */
export async function fetchCalendarSources(
  collaboratorId: string,
): Promise<CalendarSources> {
  // Pas de filtre lead_counsel_id : la policy RLS dossiers_select
  // (is_admin() OR is_dossier_member(id)) restreint déjà aux dossiers dont
  // l'utilisateur est membre (lead_counsel, counsel ou viewer). L'agenda doit
  // montrer tous les dossiers où l'avocat intervient, pas seulement ceux qu'il pilote.
  const dq = await supabase
    .from('dossiers')
    .select('id, title, next_hearing_at, prescription_date, lead_counsel_id')
    .is('archived_at', null)
    .is('closed_at', null);
  if (dq.error) throw new Error(`Failed to fetch dossiers: ${dq.error.message}`);

  const tq = await supabase
    .from('task_assignments')
    .select('task:tasks!inner(id, content, deadline, due_date, due_datetime, dossier_id, task_template_id, is_completed, is_deleted)')
    .eq('assignee_id', collaboratorId);
  if (tq.error) throw new Error(`Failed to fetch tasks: ${tq.error.message}`);

  // !inner sur une FK many-to-one : Supabase type `task` en tableau mais
  // renvoie un objet au runtime — `as unknown as` est le workaround établi
  // (cf. getMyOpenTasks dans taskService.ts).
  const tasks: TaskSrc[] = (tq.data ?? [])
    .map((r) => (r as unknown as { task: {
      id: string; content: string;
      deadline: string | null; due_date: string | null;
      due_datetime: string | null; dossier_id: string | null;
      task_template_id: string | null;
      is_completed: boolean; is_deleted: boolean;
    } | null }).task)
    .filter((t): t is NonNullable<typeof t> => !!t && !t.is_completed && !t.is_deleted)
    .map((t) => ({
      id: t.id,
      content: t.content,
      deadline: t.deadline,
      due_date: t.due_date,
      due_datetime: t.due_datetime,
      lawyerId: collaboratorId,
      dossierId: t.dossier_id,
      taskTemplateId: t.task_template_id,
    }));

  const hq = await supabase
    .from('dossier_hearings')
    .select('id, dossier_id, starts_at, label, event_type_id');
  const hearings: HearingSrc[] = hq.error
    ? []
    : (hq.data ?? []).map((h) => ({
        id: h.id,
        dossierId: h.dossier_id,
        startsAt: h.starts_at,
        label: h.label,
        eventTypeId: h.event_type_id,
      }));

  const appointments: AppointmentSrc[] = (await listAppointments()).map((a) => ({
    id: a.id,
    title: a.title,
    startsAt: a.starts_at,
    endsAt: a.ends_at,
    dossierId: a.dossier_id,
    participantIds: a.participantIds,
    eventTypeId: a.event_type_id,
  }));

  return { dossiers: (dq.data ?? []) as unknown as DossierSrc[], tasks, hearings, appointments };
}
